<template>
  <div
    class="shared-content-table"
    style="height: 100%;">
    <el-table
      style="height: 100%;"
      height="fit-scrollable"
      :data="getSharedContentItems"
      highlight-current-row
      :default-sort = "{ prop: 'displayName', order: 'ascending'}"
      @row-click="onTableRowClick"
      @select="handleSelect"
      @select-all="handleSelect"
      @sort-change="handleSortChange"
      ref="table"
      :loading-append="isTableDataLoading"
      border-bottom>

      <el-table-column
        type="selection"
      />

      <el-table-column
        prop="id"
        label="id"
        hidden/>

      <el-table-column
        prop="folderItem"
        label="folderItem"
        hidden/>

      <el-table-column
        prop="linkUrl"
        label="linkUrl"
        hidden/>

      <el-table-column
        prop="displayName"
        :label="$t('wre-dashboard.shared-content-table.column.name')"
        align="left"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          <el-icon :name="row.displayName | fileIcon(row.folderItem ? 'folder' : '')"/>
          <span class="ml-16">
            {{ row.displayName }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="status"
        align="right"
        width="48"
        label="">
        <template slot-scope="{ row }">
          <div v-if="row.passwordProtected">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-internet-access--16"/>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="boxDisplayName"
        align="left"
        :sort-method="() => 0"
        :label="$t('wre-dashboard.shared-content-table.column.source')"
        sortable/>

      <el-table-column
        prop="days"
        align="right"
        :label="$t('wre-dashboard.shared-content-table.column.days')"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          <span v-if="row.linkTtlDays > 0">
            {{ row.linkAgeDays }}<span class="lighter-text"> / {{ row.linkTtlDays }}</span>
          </span>
          <span
            class="lighter-text"
            v-else>&mdash;</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="downloads"
        align="right"
        :label="$t('wre-dashboard.shared-content-table.column.downloads')"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          <span v-if="row.hitLimit > 0">
            {{ row.hitCount }}<span class="lighter-text"> / {{ row.hitLimit }}</span>
          </span>
          <span v-else>
            {{ row.hitCount }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="creationDate"
        align="left"
        :label="$t('wre-dashboard.shared-content-table.column.shared')"
        :sort-method="() => 0"
        sortable>
        <template slot-scope="{ row }">
          {{ row.creationDate | date }}
        </template>
      </el-table-column>

    </el-table>

    <shared-link-copy-dialog-box
      v-if="isCopySharedLinkDialogVisible"
      :file-name="activeFileName"
      :link-id="activeLinkId"
      :link-url="activeLinkUrl"
      :creation-date="creationDate"
      @close="hideSharedLinkDialog"
      @settings="onLinkSettings"
    />

    <shared-link-manage-dialog-box
      v-if="isManageSharedLinkDialogVisible"
      :link-id="activeLinkId"
      @handleClose="showPreviousSharedLinkDialog"
      @handleDelete="onLinkDelete"
    />

    <shared-link-delete-dialog-box
      v-if="isDeleteSharedLinkDialogVisible"
      :link-id="activeLinkId"
      @handleClose="hideSharedLinkDialog"
      @handleCancel="showPreviousSharedLinkDialog"
    />

    <shared-link-delete-selected-dialog-box
      v-if="isDeleteSelectedSharedLinkDialogVisible"
      :link-ids="idsToDelete"
      @handleClose="hideSharedLinkDialog"
      @handleCancel="showPreviousSharedLinkDialog"
    />

    <el-details-layout
      class="el-sidebar"
      v-if="isSideBarOpen"
      open
      @close="sidebarClose()">

      <template #details>
        <el-details-panel
          @command="handleSidebarCommand"
          :title="sidebarTitle"
          :subtitle="sidebarSubTitle"
          show-close
          :actions="sidebarActions"
          :properties="sidebarProps">
          <template
            #preview
            v-if="isSideBarPreviewEnabled">
            <div class="el-sidebar-preview">
              <el-icon
                class="el-sidebar-icon"
                :name="sidebarPreviewIconName"/>
            </div>
          </template>
        </el-details-panel>
      </template>
    </el-details-layout>

  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import SharedLinkCopyDialogBox from './SharedLinkCopyDialogBox';
import SharedLinkDeleteDialogBox from './SharedLinkDeleteDialogBox';
import SharedLinkManageDialogBox from './SharedLinkManageDialogBox';
import SharedLinkDeleteSelectedDialogBox from './SharedLinkDeleteSelectedDialogBox';

import {
  COMPLETED_STATUS,
  PENDING_STATUS
} from '../store/action-status';

import {
  HIDE_ALL,
  COPY_LINK,
  MANAGE_LINK,
  DELETE_LINK,
  DELETE_SELECTED_LINKS
} from '../store/active-share-dialog';

import {
  FETCH_SHARED_CONTENT_ITEMS,
  DELETE_SHARED_LINK,
  SORT_SHARED_CONTENT_ITEMS
} from '../store/action-types';

const { mapGetters, mapActions } = createNamespacedHelpers('wre-dashboard');

export default {
  name: 'SharedContentTable',
  components: {
    SharedLinkCopyDialogBox,
    SharedLinkDeleteDialogBox,
    SharedLinkManageDialogBox,
    SharedLinkDeleteSelectedDialogBox
  },
  data() {
    return {
      previousSharedDialog: [],
      activeSharedDialog: HIDE_ALL,
      selectedItems: [],
      activeLinkId: 0,
      activeLinkUrl: '',
      creationDate: '',
      activeFileName: '',
      idsToDelete: []
    };
  },
  computed: {
    ...mapGetters([
      'getSharedContentItems',
      'getFetchSharedContentItemsStatus',
      'getUpdateSharedLinkStatus',
      'getDeleteSharedLinkStatus'
    ]),
    isTableDataLoading() {
      return this.getFetchSharedContentItemsStatus === PENDING_STATUS;
    },
    isCopySharedLinkDialogVisible() {
      return this.activeSharedDialog === COPY_LINK;
    },
    isManageSharedLinkDialogVisible() {
      return this.activeSharedDialog === MANAGE_LINK;
    },
    isDeleteSharedLinkDialogVisible() {
      return this.activeSharedDialog === DELETE_LINK;
    },
    isDeleteSelectedSharedLinkDialogVisible() {
      return this.activeSharedDialog === DELETE_SELECTED_LINKS;
    },
    isSideBarOpen() {
      return this.selectedItems.length > 0;
    },
    isSideBarPreviewEnabled() {
      return this.selectedItems.length === 1;
    },
    sidebarPreviewIconName() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        const itemName = row.displayName;
        return this.$options.filters.fileIcon(itemName, row.folderItem ? 'folder' : '');
      }
      return '';
    },
    sidebarTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.displayName;
      }
      return this.$tc(
        'wre-dashboard.side-bar.selected.title:plural',
        this.selectedItems.length, { count: this.selectedItems.length }
      );
    },
    sidebarSubTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return row.displayPath;
      }
      return '';
    },
    sidebarProps() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return [
          {
            label: this.$t('wre-dashboard.shared-content-table.label.backup'),
            value: row.boxDisplayName
          },
          {
            label: this.$t('wre-dashboard.shared-content-table.label.shared'),
            value: this.$options.filters.date(row.creationDate)
          }
        ];
      }
      return [];
    },
    sidebarActions() {
      const actions = [];

      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getMenuActions(row);
      }
      actions.push({
        label: this.$t('wre-dashboard.shared-content-table.action.delete-selected'),
        command: 'delete-selected',
        icon: 'i-trash-o--16'
      });

      return actions;
    }
  },
  watch: {
    getFetchSharedContentItemsStatus(status) {
      if (status === PENDING_STATUS) {
        this.sidebarClose();
      }
    },
    getUpdateSharedLinkStatus(status) {
      if (status === COMPLETED_STATUS) {
        this.loadTableContent();
      }
    },
    getDeleteSharedLinkStatus(status) {
      if (status === COMPLETED_STATUS) {
        this.loadTableContent();
      }
    }
  },
  methods: {
    ...mapActions({
      fetchSharedContentItems: FETCH_SHARED_CONTENT_ITEMS,
      deleteSharedLink: DELETE_SHARED_LINK,
      sortSharedContentItems: SORT_SHARED_CONTENT_ITEMS
    }),
    showSharedLinkDialog(type) {
      if (type === HIDE_ALL) {
        this.previousSharedDialog = [];
      } else {
        this.previousSharedDialog.push(this.activeSharedDialog);
      }
      this.activeSharedDialog = type;
    },
    showPreviousSharedLinkDialog() {
      const prevView = this.previousSharedDialog.pop();
      this.activeSharedDialog = (prevView !== undefined) ? prevView : HIDE_ALL;
    },
    hideSharedLinkDialog() {
      this.showSharedLinkDialog(HIDE_ALL);
    },
    handleSidebarCommand(command) {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        this.handleAction(command, row);
      } else if (command === 'delete-selected') {
        this.idsToDelete = this.selectedItems.map(function (row) { return row.id; });
        this.showSharedLinkDialog(DELETE_SELECTED_LINKS);
      }
    },
    handleAction(command, tableRow) {
      this.activeLinkId = tableRow.id;
      this.activeLinkUrl = tableRow.linkUrl;
      this.creationDate = tableRow.creationDate;
      this.activeFileName = tableRow.displayName;

      if (command === 'share') {
        this.showSharedLinkDialog(COPY_LINK);

        // this.$gtm.push({
        //   event: 'Acronis',
        //   eventCategory: 'WR_sharing',
        //   eventAction: 'Share_link',
        //   eventLabel: ''
        // });
      } else if (command === 'settings') {
        this.onLinkSettings(this.activeLinkId);

        // this.$gtm.push({
        //   event: 'Acronis',
        //   eventCategory: 'WR_sharing',
        //   eventAction: 'Link_settings',
        //   eventLabel: ''
        // });
      } else if (command === 'delete') {
        this.onLinkDelete(this.activeLinkId);

        // this.$gtm.push({
        //   event: 'Acronis',
        //   eventCategory: 'WR_sharing',
        //   eventAction: 'Delete_link',
        //   eventLabel: ''
        // });
      }
    },
    onLinkSettings(linkId) {
      this.activeLinkId = linkId;
      this.showSharedLinkDialog(MANAGE_LINK);
    },
    onLinkDelete(linkId) {
      this.activeLinkId = linkId;
      this.showSharedLinkDialog(DELETE_LINK);
    },
    getMenuActions(row) {
      const actions = [];

      actions.push({
        label: this.$t('wre-dashboard.shared-content-table.action.share'),
        command: 'share',
        icon: 'i-share--16'
      });

      actions.push({
        label: this.$t('wre-dashboard.shared-content-table.action.settings'),
        command: 'settings',
        icon: 'i-cog--16'
      });

      actions.push({
        label: this.$t('wre-dashboard.shared-content-table.action.delete'),
        command: 'delete',
        icon: 'i-trash-o--16'
      });

      return actions;
    },
    sidebarClose() {
      this.$refs.table.clearSelection();
      this.selectedItems = [];
    },
    handleSelect(selectedItems) {
      this.selectedItems = selectedItems;
    },
    onTableRowClick(row, event, column) {
      this.$refs.table.clearSelection();
      this.selectedItems = [row];
    },
    loadTableContent() {
      this.fetchSharedContentItems();
    },
    handleSortChange(column) {
      this.sortSharedContentItems({
        items: this.getSharedContentItems,
        columnName: column.prop,
        sortOrder: column.order
      });
    }
  },
  mounted() {
    this.loadTableContent();
  }
};
</script>

<style lang="scss" scoped>
  .lighter-text {
    color: $av-fixed-lighter;
  }
</style>
